/**
 * Gerador de números pseudoaleatórios determinístico.
 *
 * Todo sorteio do jogo — acerto, crítico, subida de nível — passa por aqui. O
 * estado é um único inteiro de 32 bits, o que torna a partida inteira
 * reproduzível a partir da semente mais a lista de ações: é o que sustenta
 * replay, verificação antifraude no servidor e bug que vira teste.
 *
 * Algoritmo: splitmix32. Escolhido pelo estado mínimo (um uint32, que
 * serializa em um campo) e por passar nos testes estatísticos que importam
 * para um jogo — não é, nem precisa ser, criptográfico.
 */
export class Rng {
    #state;
    constructor(seed) {
        this.#state = seed >>> 0;
    }
    /** Recria um gerador a partir de um estado serializado. */
    static fromState(state) {
        return new Rng(state);
    }
    /** Estado atual, para serializar dentro do estado da partida. */
    get state() {
        return this.#state;
    }
    /** Cópia independente — útil para simular sem consumir o gerador real. */
    clone() {
        return new Rng(this.#state);
    }
    /** Próximo inteiro sem sinal de 32 bits. */
    next() {
        this.#state = (this.#state + 0x9e3779b9) >>> 0;
        let z = this.#state;
        z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
        z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
        return (z ^ (z >>> 15)) >>> 0;
    }
    /** Fração em [0, 1). */
    float() {
        return this.next() / 0x1_0000_0000;
    }
    /** Inteiro em [0, maxExclusive). */
    int(maxExclusive) {
        if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
            throw new RangeError(`maxExclusive precisa ser inteiro positivo, veio ${maxExclusive}`);
        }
        return Math.floor(this.float() * maxExclusive);
    }
    /** Inteiro em [min, max], ambos inclusive. */
    range(min, max) {
        if (max < min)
            throw new RangeError(`intervalo invertido: [${min}, ${max}]`);
        return min + this.int(max - min + 1);
    }
    /**
     * Sorteio simples: `percent` de chance em 100.
     * Usado onde o Fire Emblem usa um dado só — crítico, ativação de habilidade.
     */
    chance(percent) {
        if (percent <= 0)
            return false;
        if (percent >= 100)
            return true;
        return this.int(100) < percent;
    }
    /**
     * Sorteio de dois dados, o "2RN" dos Fire Emblem de GBA.
     *
     * A taxa de acerto exibida na tela é comparada com a MÉDIA de dois sorteios,
     * não com um. O efeito é característico da série: acima de 50% o acerto real
     * fica melhor do que o número mostrado, abaixo de 50% fica pior — o que faz
     * o jogo parecer mais justo do que uma moeda honesta seria.
     */
    chance2rn(percent) {
        if (percent <= 0)
            return false;
        if (percent >= 100)
            return true;
        const roll = (this.int(100) + this.int(100)) / 2;
        return roll < percent;
    }
    /** Sorteia um elemento do vetor. Lança se o vetor estiver vazio. */
    pick(items) {
        if (items.length === 0)
            throw new RangeError('pick() recebeu um vetor vazio');
        return items[this.int(items.length)];
    }
    /** Embaralhamento Fisher–Yates, devolvendo uma cópia. */
    shuffle(items) {
        const out = [...items];
        for (let i = out.length - 1; i > 0; i--) {
            const j = this.int(i + 1);
            [out[i], out[j]] = [out[j], out[i]];
        }
        return out;
    }
}
//# sourceMappingURL=rng.js.map